import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CAlert, CButton, CFormCheck, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle } from '@coreui/react';
import { api, problemMessage, type Schemas } from '../api/client';
import { useMe, useSetMe } from '../auth/AuthGate';
import { openPractice } from '../tour/PracticeCoach';
import { setWelcomeOpen } from '../tour/tour';

const dismissedKey = 'contracts.welcome.dismissed';

/**
 * Uvítání pro toho, kdo ještě nemá žádnou roli — tedy typicky při prvním přihlášení.
 * Bez role mu nechodí práce určená roli (kontrola pro vedení, dotazy na výrobu), proto
 * je to první otázka. Potom nabídne cvičnou zakázku.
 * <p>
 * „Později“ ho schová jen do zavření prohlížeče — role jsou důležité, ať se to vrátí.
 */
export function WelcomeModal() {
  const me = useMe();
  const setMe = useSetMe();
  const navigate = useNavigate();
  const [step, setStep] = useState<'roles' | 'practice' | null>(() => {
    try {
      return me.roles.length === 0 && window.sessionStorage.getItem(dismissedKey) !== '1' ? 'roles' : null;
    } catch {
      return me.roles.length === 0 ? 'roles' : null;
    }
  });
  const [roles, setRoles] = useState<Schemas['DomainRoleResponse'][]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setWelcomeOpen(step !== null), [step]);

  useEffect(() => {
    if (step === 'roles') api.GET('/api/v1/team/roles').then(({ data }) => setRoles(data ?? []));
  }, [step]);

  function close(showTour = true) {
    // CModal vola onClose jeste jednou, kdyz se po zavreni schova - to uz nic nedela.
    // Jinak by po "Vyzkoušet" naskocila prohlidka Moje prace pres cvicnou zakazku.
    if (step === null) return;
    try { window.sessionStorage.setItem(dismissedKey, '1'); } catch { /* jen pohodli */ }
    setStep(null);
    setWelcomeOpen(false);
    // Moje prace s prohlidkou obrazovky cekala, az uvitani skonci. Kdo jde cvicit, toho
    // povede kouc cvicne zakazky - dve prohlidky naraz by se prekryvaly.
    if (showTour) window.dispatchEvent(new Event('contracts:welcome-done'));
  }

  async function saveRoles() {
    const { data, error: problem } = await api.PUT('/api/v1/team/me/roles', { body: { roleCodes: picked } });

    if (!data) {
      setError(problemMessage(problem));
      return;
    }

    setMe({ ...me, roles: data });
    setStep('practice');
  }

  async function practice() {
    close(false);
    await openPractice(navigate);
  }

  const toggle = (code: string, on: boolean) => setPicked(on ? [...picked, code] : picked.filter((c) => c !== code));

  return (
    <CModal visible={step !== null} onClose={() => close()} backdrop="static">
      {step === 'roles' && (
        <>
          <CModalHeader><CModalTitle>Vítejte v Zakázkách</CModalTitle></CModalHeader>
          <CModalBody>
            <p>Než začnete: <strong>co děláte?</strong> Podle toho vám bude chodit práce — třeba zakázky ke kontrole, když jste ve vedení.</p>
            {roles.map((r) => (
              <CFormCheck key={r.code} id={`welcome-${r.code}`} label={r.name}
                checked={picked.includes(r.code)} onChange={(e) => toggle(r.code, e.target.checked)} />
            ))}
            <div className="small text-body-secondary mt-2">Kdykoli to změníte v Nastavení. Nic to nepovoluje ani nezakazuje.</div>
            {error && <CAlert color="danger" className="mt-3 mb-0">{error}</CAlert>}
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" variant="ghost" onClick={() => close()}>Později</CButton>
            <CButton color="primary" disabled={picked.length === 0} onClick={() => void saveRoles()}>Pokračovat</CButton>
          </CModalFooter>
        </>
      )}
      {step === 'practice' && (
        <>
          <CModalHeader><CModalTitle>Vyzkoušíte si to?</CModalTitle></CModalHeader>
          <CModalBody>
            Na <strong>cvičné zakázce</strong> si za pár minut projdete celý průběh — od telefonátu
            po předání zákazníkovi. Zahrajete si všechny role. Vidíte ji jen vy, nic se nikomu
            neodešle a na konci se smaže.
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" variant="ghost" onClick={() => close()}>Teď ne</CButton>
            <CButton color="primary" onClick={() => void practice()}>Vyzkoušet</CButton>
          </CModalFooter>
        </>
      )}
    </CModal>
  );
}
